import { supabase } from './supabase';

/**
 * Validates registration data
 * @param {Object} data Registration form data
 * @returns {Object} Validation result { isValid: boolean, error: string | null }
 */
const validateRegistrationData = (data) => {
  const { full_name, account_type, address, phone, password, confirm_password } = data;

  // Check required fields
  if (!full_name?.trim()) {
    return { isValid: false, error: "الرجاء إدخال الاسم الكامل" };
  }

  if (!account_type?.trim()) {
    return { isValid: false, error: "الرجاء اختيار نوع الحساب" };
  }

  if (!address?.trim()) {
    return { isValid: false, error: "الرجاء إدخال العنوان" };
  }

  if (!phone?.trim()) {
    return { isValid: false, error: "الرجاء إدخال رقم الهاتف" };
  }

  // Validate phone number format
  const phoneRegex = /^01[0125][0-9]{8}$/;
  if (!phoneRegex.test(phone)) {
    return { isValid: false, error: "رقم الهاتف غير صحيح" };
  }

  if (!password) {
    return { isValid: false, error: "الرجاء إدخال كلمة المرور" };
  }

  if (password.length < 6) {
    return { isValid: false, error: "كلمة المرور يجب أن تكون 6 أحرف على الأقل" };
  }

  if (password !== confirm_password) {
    return { isValid: false, error: "كلمات المرور غير متطابقة" };
  }

  return { isValid: true, error: null };
};

/**
 * Handles user registration
 * @param {Object} registrationData User registration data
 * @returns {Promise<Object>} Registration result
 */
export const registerUser = async (registrationData) => {
  try {
    // Validate input data
    const validation = validateRegistrationData(registrationData);
    if (!validation.isValid) {
      return {
        status: 'error',
        message: validation.error
      };
    }

    const { full_name, account_type, address, phone, password } = registrationData;

    // First, check if phone number already exists
    const { data: existingUser } = await supabase
      .from('users')
      .select('phone')
      .eq('phone', phone)
      .single();

    if (existingUser) {
      return {
        status: 'error',
        message: 'رقم الهاتف مسجل بالفعل'
      };
    }

    // Create user in Supabase
    const { data, error } = await supabase
      .from('users')
      .insert([
        {
          full_name,
          account_type,
          address,
          phone,
          password, // Plain text password
          created_at: new Date().toISOString()
        }
      ])
      .single();

    if (error) {
      console.error('Registration error:', error.message);
      return {
        status: 'error',
        message: 'حدث خطأ أثناء إنشاء الحساب. الرجاء المحاولة مرة أخرى.'
      };
    }

    return {
      status: 'success',
      message: 'تم إنشاء الحساب بنجاح! يمكنك الآن تسجيل الدخول.'
    };

  } catch (error) {
    console.error('Unexpected error during registration:', error.message);
    return {
      status: 'error',
      message: 'حدث خطأ غير متوقع. الرجاء المحاولة مرة أخرى.'
    };
  }
};

/**
 * Logs in a user
 * @param {string} phone User's phone number
 * @param {string} password User's password
 * @returns {Promise<Object>} Login result
 */
export const loginUser = async (phone, password) => {
  try {
    // First get user data with phone number
    const { data, error: fetchError } = await supabase
      .from('users')
      .select('id, full_name, account_type, phone, password')
      .eq('phone', phone);

    if (fetchError) {
      console.error('Error fetching user:', fetchError.message);
      return {
        status: 'error',
        message: 'حدث خطأ أثناء تسجيل الدخول'
      };
    }

    // Check if we found a user
    if (!data || data.length === 0) {
      return {
        status: 'error',
        message: 'رقم الهاتف أو كلمة المرور غير صحيحة'
      };
    }

    const user = data[0];

    // Compare passwords directly
    if (String(password).trim() !== String(user.password).trim()) {
      return {
        status: 'error',
        message: 'رقم الهاتف أو كلمة المرور غير صحيحة'
      };
    }

    const userData = {
      id: user.id,
      full_name: user.full_name,
      account_type: user.account_type,
      phone: user.phone
    };

    // Save auth token to localStorage
    localStorage.setItem('auth_token', Date.now().toString());

    return {
      status: 'success',
      message: 'تم تسجيل الدخول بنجاح',
      data: {
        user: userData
      }
    };

  } catch (error) {
    console.error('Login error:', error.message);
    return {
      status: 'error',
      message: 'حدث خطأ أثناء تسجيل الدخول. الرجاء المحاولة مرة أخرى.'
    };
  }
};
